import {
	type Card as FsrsCard,
	createEmptyCard,
	fsrs,
	generatorParameters,
	type Grade,
	State,
} from "ts-fsrs";
import type { Card, CardState } from "./db";

// Flashcard scheduling (ST-3, D58): ts-fsrs defaults (FSRS-6, fuzz off,
// learning steps 1m 10m, relearning 10m, 90% retention). The Go port in
// apps/api/internal/study/fsrs is tested against the same fixture.

const scheduler = fsrs(generatorParameters({ enable_fuzz: false }));

/** The columns of a card row that the scheduler owns. */
export type SchedulerFields = Pick<
	Card,
	| "state"
	| "due_at"
	| "stability"
	| "difficulty"
	| "elapsed_days"
	| "scheduled_days"
	| "learning_steps"
	| "reps"
	| "lapses"
	| "last_review"
>;

export type Rating = 1 | 2 | 3 | 4;
export const RATINGS: readonly Rating[] = [1, 2, 3, 4];

export const RATING_LABELS: Record<Rating, string> = {
	1: "Again",
	2: "Hard",
	3: "Good",
	4: "Easy",
};

/** A brand new card, due now. */
export function newCardFields(now: Date): SchedulerFields {
	return fromFsrs(createEmptyCard(now));
}

export function toFsrs(c: SchedulerFields): FsrsCard {
	return {
		due: new Date(c.due_at),
		stability: c.stability,
		difficulty: c.difficulty,
		elapsed_days: c.elapsed_days,
		scheduled_days: c.scheduled_days,
		learning_steps: c.learning_steps,
		reps: c.reps,
		lapses: c.lapses,
		state: c.state as State,
		last_review: c.last_review ? new Date(c.last_review) : undefined,
	};
}

export function fromFsrs(c: FsrsCard): SchedulerFields {
	return {
		state: c.state as CardState,
		due_at: c.due.toISOString(),
		stability: c.stability,
		difficulty: c.difficulty,
		elapsed_days: c.elapsed_days,
		scheduled_days: c.scheduled_days,
		learning_steps: c.learning_steps,
		reps: c.reps,
		lapses: c.lapses,
		last_review: c.last_review ? c.last_review.toISOString() : null,
	};
}

/** The card after one review. `scheduled_days` is also what card_reviews records. */
export function reviewCard(card: SchedulerFields, rating: Rating, now: Date): SchedulerFields {
	return fromFsrs(scheduler.next(toFsrs(card), now, rating as Grade).card);
}

/** When each rating would make the card due again (for the button hints). */
export function previewDue(card: SchedulerFields, now: Date): Record<Rating, Date> {
	const preview = scheduler.repeat(toFsrs(card), now);
	return {
		1: preview[1].card.due,
		2: preview[2].card.due,
		3: preview[3].card.due,
		4: preview[4].card.due,
	};
}

/** `10m`, `6h`, `3d`, `2mo`, `1.5y`: how far away a due time is. */
export function formatInterval(from: Date, due: Date): string {
	const min = Math.max(0, Math.round((due.getTime() - from.getTime()) / 60_000));
	if (min < 60) return `${Math.max(1, min)}m`;
	const hours = Math.round(min / 60);
	if (hours < 24) return `${hours}h`;
	const days = Math.round(min / 1440);
	if (days < 30) return `${days}d`;
	if (days < 365) return `${Math.round(days / 30)}mo`;
	return `${(Math.round((days / 365) * 10) / 10).toString()}y`;
}

/** Cards that are due: not archived or deleted, with a due time at or before `now`. */
export function isDue(c: Pick<Card, "due_at" | "archived" | "deleted_at">, now: Date): boolean {
	return !c.archived && !c.deleted_at && Date.parse(c.due_at) <= now.getTime();
}
